import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Input, Pagination } from 'antd';
import { useIntl } from 'react-intl';

import AuditLogTable from '@src/components/AuditLogTable';
import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ComponentBlocker from '@src/components/ComponentBlocker';
import FilterContainer from '@src/components/FilterContainer';
import { useAuditLogs } from '@src/hooks/useAuditLogs';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { asAllowedRoles } from '@src/packages/utils/access';

import styles from './styles.module.less';

interface AuditLogViewerProps {
  entityType: string;
  entityId: string;
  recordLabel?: string;
  triggerButton?: React.ReactElement<{ onClick?: () => void }>;
  open?: boolean;
  onClose?: () => void;
}

const AUDIT_LOG_ROLES = asAllowedRoles(ALLOWED_ROLES.ORGANIZATIONS.AUDIT_LOGS.GET);

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  entityType,
  entityId,
  recordLabel,
  triggerButton,
  open,
  onClose,
}) => {
  const intl = useIntl();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const {
    logs,
    total,
    loading,
    error,
    currentPage,
    pageSize,
    search,
    changePage,
    retry,
    reset,
  } = useAuditLogs({
    enabled: isOpen,
    entityType,
    entityId,
    initialPageSize: 10,
  });

  const handleOpen = useCallback((): void => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  }, [isControlled]);
  const close = useCallback((): void => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onClose?.();
    reset();
  }, [isControlled, onClose, reset]);

  const trigger = useMemo(
    () =>
      triggerButton ? (
        React.cloneElement(triggerButton, { onClick: handleOpen })
      ) : (
        <Button onClick={handleOpen}>
          {intl.formatMessage({ id: 'auditLogViewer.buttonText' })}
        </Button>
      ),
    [handleOpen, intl, triggerButton],
  );
  const title = recordLabel
    ? intl.formatMessage({ id: 'auditLogViewer.titleWithRecord' }, { name: recordLabel })
    : intl.formatMessage({ id: 'auditLogViewer.title' });

  return (
    <>
      {!isControlled && (
        <ComponentBlocker allowedRoles={AUDIT_LOG_ROLES}>{trigger}</ComponentBlocker>
      )}

      <ComponentBlocker allowedRoles={AUDIT_LOG_ROLES}>
        <Drawer
          className={styles.drawer}
          closable={false}
          destroyOnClose
          footer={null}
          onClose={close}
          open={isOpen}
          placement="right"
          title={null}
          width={760}
          zIndex={1100}
        >
          <BoxContainer>
            <BoxContainer content fixed bottomBorder>
              <FilterContainer
                goBack={close}
                title={<Title.Header value={title} />}
              />
            </BoxContainer>

            <div className={styles.searchContainer}>
              <Input.Search
                allowClear
                className={styles.searchInput}
                loading={loading}
                onSearch={search}
                placeholder={intl.formatMessage({ id: 'auditLogViewer.search.placeholder' })}
              />
            </div>

            <div className={styles.auditLogViewer}>
              {error ? (
                <Alert
                  action={
                    <Button onClick={retry} size="small">
                      {intl.formatMessage({ id: 'auditLogs.retry' })}
                    </Button>
                  }
                  message={intl.formatMessage({ id: 'auditLogs.error' })}
                  showIcon
                  type="error"
                />
              ) : (
                <AuditLogTable
                  logs={logs}
                  loading={loading}
                  maxHeight="calc(100vh - 238px)"
                  showEntityLabel={false}
                />
              )}
            </div>

            {!error && total > 0 ? (
              <div className={styles.paginationContainer}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  pageSizeOptions={['10', '20', '50', '100']}
                  showSizeChanger
                  showTotal={(count, range) =>
                    intl.formatMessage(
                      { id: 'auditLogs.pagination.total' },
                      { start: range[0], end: range[1], total: count },
                    )
                  }
                  total={total}
                  onChange={changePage}
                />
              </div>
            ) : null}
          </BoxContainer>
        </Drawer>
      </ComponentBlocker>
    </>
  );
};

export default AuditLogViewer;
